import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { FileText, Music, Video, Download, Play, Eye, Folder, FolderOpen, ChevronRight, ChevronDown, ChevronUp, Home, HelpCircle, BookOpen, Disc, Lock, Sparkles, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import MediaViewer from '../components/MediaViewer';
import UpgradePrompt from '../components/UpgradePrompt';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tab configuration - 5 tabs
const TABS = [
  { id: 'video-tutorials', name: 'Video Tutorials', icon: Video, min_tier: 'starter' },
  { id: 'course-documents', name: 'Course Documents', icon: FileText, min_tier: 'starter' },
  { id: 'quizzes', name: 'Quizzes', icon: HelpCircle, min_tier: 'starter' },
  { id: 'music', name: 'Music', icon: Music, min_tier: 'pro' },
  { id: 'ebooks', name: 'Ebooks', icon: BookOpen, min_tier: 'elite' }
];

const ContentLibrary = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('video-tutorials');
  const [tabData, setTabData] = useState({});
  const [loading, setLoading] = useState(true);
  const [tabLoading, setTabLoading] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerData, setViewerData] = useState({ url: '', type: 'video', title: '' });
  const [recentlyAdded, setRecentlyAdded] = useState({ lessons: [], content: [] });
  const [recentlyAddedExpanded, setRecentlyAddedExpanded] = useState(false);
  
  // Folder navigation state per tab
  const [folderState, setFolderState] = useState({});

  // Check if user is admin
  const isAdmin = user?.is_admin || user?.email === 'contact@blendonics.com';
  
  // Get effective tier
  const getEffectiveTier = useCallback(() => {
    if (isAdmin) return 'elite';
    if (user?.membership_status === 'lifetime' && user?.has_course_access) return 'starter';
    if (user?.membership_status === 'active' || user?.membership_status === 'cancelling') {
      return user?.membership_tier || 'free';
    }
    return 'free';
  }, [isAdmin, user]);
  
  const tier = getEffectiveTier();
  const tierLevels = { 'free': 0, 'starter': 1, 'pro': 2, 'elite': 3 };
  const userLevel = tierLevels[tier] || 0;
  const canAccessContent = tier !== 'free';

  // Check if user can access a specific tier
  const canAccessTier = useCallback((requiredTier) => {
    const requiredLevel = tierLevels[requiredTier] || 0;
    return userLevel >= requiredLevel;
  }, [userLevel]);

  // Fetch recently added content
  const fetchRecentlyAdded = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/content/recently-added`);
      setRecentlyAdded(response.data);
    } catch (error) {
      console.error('Failed to load recently added:', error);
    }
  }, []);

  // Fetch content for a specific tab
  const fetchTabContent = useCallback(async (tabId) => {
    if (!tabId || tabData[tabId]) return;
    
    setTabLoading(true);
    try {
      const response = await axios.get(`${API}/tabs/${tabId}`);
      setTabData(prev => ({
        ...prev,
        [tabId]: {
          folders: response.data.folders || [],
          lessons: response.data.lessons || [],
          content: response.data.content || []
        }
      }));
      // Initialize folder state for this tab
      if (!folderState[tabId]) {
        setFolderState(prev => ({
          ...prev,
          [tabId]: { folderId: null, folderPath: [] }
        }));
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(`This tab requires a higher membership tier`);
      } else {
        console.error('Failed to load tab content:', error);
        toast.error('Failed to load content');
      }
    } finally {
      setTabLoading(false);
    }
  }, [tabData, folderState]);

  useEffect(() => {
    if (canAccessContent) {
      fetchRecentlyAdded();
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [canAccessContent, fetchRecentlyAdded]);

  // Fetch content when tab changes
  useEffect(() => {
    if (activeTab && canAccessContent) {
      fetchTabContent(activeTab);
    }
  }, [activeTab, canAccessContent, fetchTabContent]);

  // Get current folder state for active tab
  const currentFolderState = folderState[activeTab] || { folderId: null, folderPath: [] };
  const currentFolderId = currentFolderState.folderId;
  const currentFolderPath = currentFolderState.folderPath;

  // Navigate to a folder
  const navigateToFolder = (folder) => {
    setFolderState(prev => ({
      ...prev,
      [activeTab]: {
        folderId: folder.id,
        folderPath: [...(prev[activeTab]?.folderPath || []), folder]
      }
    }));
  };

  // Navigate via breadcrumb
  const navigateToBreadcrumb = (index) => {
    if (index === -1) {
      setFolderState(prev => ({
        ...prev,
        [activeTab]: { folderId: null, folderPath: [] }
      }));
    } else {
      const newPath = currentFolderPath.slice(0, index + 1);
      setFolderState(prev => ({
        ...prev,
        [activeTab]: { folderId: newPath[newPath.length - 1].id, folderPath: newPath }
      }));
    }
  };

  // Get folders for current tab and location
  const getCurrentFolders = useCallback(() => {
    const data = tabData[activeTab];
    if (!data || !data.folders) return [];
    
    const folderId = currentFolderId;
    
    return data.folders.filter(f => {
      if (folderId === null) {
        return !f.parent_folder_id;
      }
      return f.parent_folder_id === folderId;
    });
  }, [tabData, activeTab, currentFolderId]);

  // Get content for current tab and folder
  const getCurrentContent = useCallback(() => {
    const data = tabData[activeTab];
    if (!data) return { lessons: [], content: [] };
    
    const folderId = currentFolderId;
    
    const lessons = (data.lessons || []).filter(l => {
      if (folderId === null) {
        return !l.folder_id;
      }
      return l.folder_id === folderId;
    });
    
    const content = (data.content || []).filter(c => {
      if (folderId === null) {
        return !c.folder_id;
      }
      return c.folder_id === folderId;
    });
    
    return { lessons, content };
  }, [tabData, activeTab, currentFolderId]);

  const getIcon = (type) => {
    switch (type) {
      case 'video': return <Video size={24} style={{ color: '#D4AF37' }} />;
      case 'audio': return <Music size={24} style={{ color: '#D4AF37' }} />;
      case 'pdf': return <FileText size={24} style={{ color: '#D4AF37' }} />;
      case 'quiz': return <HelpCircle size={24} style={{ color: '#D4AF37' }} />;
      default: return <Download size={24} style={{ color: '#D4AF37' }} />;
    }
  };

  // Helper function for tier badge styling
  const getTierBadgeStyle = (tierName) => {
    switch (tierName) {
      case 'elite':
        return { background: 'rgba(168, 85, 247, 0.2)', color: '#a855f7' };
      case 'pro':
        return { background: 'rgba(59, 130, 246, 0.2)', color: '#3b82f6' };
      case 'starter':
      default:
        return { background: 'rgba(34, 197, 94, 0.2)', color: '#22c55e' };
    }
  };

  const openViewer = (url, type, title) => {
    setViewerData({ url, type, title });
    setViewerOpen(true);
  };

  // Show upgrade prompt for free users
  if (!canAccessContent) {
    return <UpgradePrompt feature="content" currentTier={tier} requiredTier="starter" />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading content...</div>
      </div>
    );
  }

  const folders = getCurrentFolders();
  const { lessons, content } = getCurrentContent();
  const currentTab = TABS.find(t => t.id === activeTab);
  const tabAccessible = currentTab ? canAccessTier(currentTab.min_tier) : false;

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8" style={{ background: '#0a0a0a' }}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2" style={{ color: '#ffffff' }}>Content Library</h1>
          <p style={{ color: '#a3a3a3' }}>Access your DJ learning materials</p>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="tabs-container">
          {TABS.map((tab) => {
            const TabIcon = tab.icon;
            const isAccessible = canAccessTier(tab.min_tier);
            const isActive = activeTab === tab.id;
            
            return (
              <button
                key={tab.id}
                onClick={() => isAccessible && setActiveTab(tab.id)}
                disabled={!isAccessible}
                className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-200 ${
                  isActive 
                    ? 'border-2' 
                    : 'border border-gray-700 hover:border-gray-500'
                } ${!isAccessible ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                style={{
                  borderColor: isActive ? '#D4AF37' : undefined,
                  background: isActive ? 'rgba(212, 175, 55, 0.1)' : 'transparent',
                  color: isActive ? '#D4AF37' : '#a3a3a3'
                }}
                data-testid={`tab-${tab.id}`}
              >
                <TabIcon size={18} />
                <span className="text-sm font-medium">{tab.name}</span>
                {!isAccessible && <Lock size={14} />}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        {!tabAccessible ? (
          <div className="text-center py-16">
            <Lock size={48} className="mx-auto mb-4" style={{ color: '#666' }} />
            <h3 className="text-xl font-semibold mb-2" style={{ color: '#ffffff' }}>
              {currentTab?.name} - Upgrade Required
            </h3>
            <p className="mb-6" style={{ color: '#a3a3a3' }}>
              This content requires {currentTab?.min_tier} membership or higher.
            </p>
            <Button
              onClick={() => window.location.href = '/pricing'}
              style={{ background: '#D4AF37', color: '#000' }}
            >
              Upgrade Now
            </Button>
          </div>
        ) : tabLoading && !tabData[activeTab] ? (
          <div className="text-center py-12">
            <div className="text-lg" style={{ color: '#a3a3a3' }}>Loading content...</div>
          </div>
        ) : (
          <div data-testid="tab-content">
            {/* Breadcrumb */}
            <div className="mb-6 flex items-center gap-2 text-sm" style={{ color: '#a3a3a3' }}>
              <button
                onClick={() => navigateToBreadcrumb(-1)}
                className="flex items-center gap-1 hover:text-white transition-colors"
                data-testid="breadcrumb-home"
              >
                <Home size={16} />
                All Content
              </button>
              {currentFolderPath.map((folder, index) => (
                <span key={folder.id} className="flex items-center gap-2">
                  <ChevronRight size={14} />
                  <button
                    onClick={() => navigateToBreadcrumb(index)}
                    className="hover:text-white transition-colors"
                  >
                    {folder.name}
                  </button>
                </span>
              ))}
            </div>

            {/* Folders Section */}
            <div className="mb-6" data-testid="folders-section">
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2" style={{ color: '#ffffff' }}>
                <FolderOpen size={20} style={{ color: '#D4AF37' }} />
                Folders
              </h3>
              {folders.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {folders.map((folder) => (
                    <button
                      key={folder.id}
                      onClick={() => navigateToFolder(folder)}
                      className="rounded-xl border-2 bg-card p-4 text-left transition-all duration-300 hover:border-yellow-500/50 hover:scale-[1.02] group"
                      style={{ borderColor: 'rgba(212, 175, 55, 0.3)' }}
                      data-testid={`folder-${folder.id}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <div className="p-1.5 rounded-lg" style={{ background: 'rgba(212, 175, 55, 0.1)' }}>
                          <Folder size={18} className="group-hover:hidden" style={{ color: '#D4AF37' }} />
                          <FolderOpen size={18} className="hidden group-hover:block" style={{ color: '#D4AF37' }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-sm truncate" style={{ color: '#ffffff' }}>{folder.name}</h4>
                          <span className="text-xs px-1.5 py-0.5 rounded-full" style={getTierBadgeStyle(folder.tier_required)}>
                            {folder.tier_required}
                          </span>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="p-4 rounded-xl border border-dashed text-center" style={{ borderColor: 'rgba(212, 175, 55, 0.3)' }}>
                  <p className="text-sm" style={{ color: '#a3a3a3' }}>No folders in this tab</p>
                </div>
              )}
            </div>

            {/* Recently Added Section - Only at root level */}
            {currentFolderId === null && (recentlyAdded.lessons?.length > 0 || recentlyAdded.content?.length > 0) && (
              <div className="mb-8 rounded-xl border overflow-hidden" style={{ borderColor: 'rgba(212, 175, 55, 0.2)', background: 'rgba(212, 175, 55, 0.05)' }}>
                <button
                  onClick={() => setRecentlyAddedExpanded(!recentlyAddedExpanded)}
                  className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
                  data-testid="recently-added-toggle"
                >
                  <h3 className="text-lg font-semibold flex items-center gap-2" style={{ color: '#D4AF37' }}>
                    <Sparkles size={20} />
                    Recently Added
                    <span className="text-sm font-normal" style={{ color: '#a3a3a3' }}>
                      ({(recentlyAdded.lessons?.length || 0) + (recentlyAdded.content?.length || 0)} items)
                    </span>
                  </h3>
                  <div className="flex items-center gap-2">
                    <span className="text-xs" style={{ color: '#a3a3a3' }}>
                      {recentlyAddedExpanded ? 'Collapse' : 'Expand'}
                    </span>
                    {recentlyAddedExpanded ? (
                      <ChevronUp size={20} style={{ color: '#D4AF37' }} />
                    ) : (
                      <ChevronDown size={20} style={{ color: '#D4AF37' }} />
                    )}
                  </div>
                </button>
                
                {recentlyAddedExpanded && (
                  <div className="p-4 pt-0 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[
                      ...(recentlyAdded.lessons || []).map(l => ({...l, itemType: 'lesson'})),
                      ...(recentlyAdded.content || []).map(c => ({...c, itemType: 'content'}))
                    ].slice(0, 6).map((item) => (
                      <div
                        key={item.id}
                        className="rounded-xl border border-border bg-card p-4 flex items-center gap-3 transition-all duration-300 hover:border-yellow-500/30"
                      >
                        <div className="p-2 rounded-lg" style={{ background: 'rgba(212, 175, 55, 0.1)' }}>
                          {item.itemType === 'lesson' ? <Video size={20} style={{ color: '#D4AF37' }} /> : getIcon(item.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <h4 className="font-medium text-sm truncate" style={{ color: '#ffffff' }}>{item.title}</h4>
                            <span className="text-xs px-1.5 py-0.5 rounded-full flex-shrink-0" style={getTierBadgeStyle(item.tier_required)}>
                              {item.tier_required}
                            </span>
                          </div>
                          <p className="text-xs flex items-center gap-1" style={{ color: '#a3a3a3' }}>
                            <Clock size={12} />
                            {item.itemType === 'lesson' ? 'Video' : item.type?.toUpperCase()}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => {
                            if (item.itemType === 'lesson') {
                              openViewer(item.video_url, 'video', item.title);
                            } else if (item.type === 'quiz') {
                              window.open(item.url, '_blank');
                            } else if (item.type === 'pdf') {
                              window.open(item.url, '_blank');
                            } else {
                              openViewer(item.url, item.type, item.title);
                            }
                          }}
                          className="flex-shrink-0"
                          style={{ background: 'rgba(212, 175, 55, 0.2)', color: '#D4AF37' }}
                        >
                          <Play size={14} />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Content Section */}
            <div data-testid="content-section">
              {lessons.length > 0 || content.length > 0 ? (
                <div className="space-y-4">
                  {lessons.map((lesson) => (
                    <div
                      key={lesson.id}
                      className="rounded-xl border border-border bg-card p-4 flex items-center gap-4 transition-all duration-300 hover:border-yellow-500/30"
                      data-testid={`lesson-${lesson.id}`}
                    >
                      <div className="p-3 rounded-lg" style={{ background: 'rgba(212, 175, 55, 0.1)' }}>
                        <Video size={24} style={{ color: '#D4AF37' }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold truncate" style={{ color: '#ffffff' }}>{lesson.title}</h4>
                          <span className="text-xs px-2 py-0.5 rounded-full flex-shrink-0" style={getTierBadgeStyle(lesson.tier_required)}>
                            {lesson.tier_required}
                          </span>
                        </div>
                        {lesson.duration && (
                          <p className="text-sm" style={{ color: '#a3a3a3' }}>{lesson.duration}</p>
                        )}
                      </div>
                      <Button
                        size="sm"
                        onClick={() => openViewer(lesson.video_url, 'video', lesson.title)}
                        className="flex items-center gap-2"
                        style={{ background: 'rgba(212, 175, 55, 0.2)', color: '#D4AF37' }}
                      >
                        <Play size={16} /> Watch
                      </Button>
                    </div>
                  ))}
                  
                  {content.map((item) => (
                    <div
                      key={item.id}
                      className="rounded-xl border border-border bg-card p-4 flex items-center gap-4 transition-all duration-300 hover:border-yellow-500/30"
                      data-testid={`content-${item.id}`}
                    >
                      <div className="p-3 rounded-lg" style={{ background: 'rgba(212, 175, 55, 0.1)' }}>
                        {getIcon(item.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold truncate" style={{ color: '#ffffff' }}>{item.title}</h4>
                          <span className="text-xs px-2 py-0.5 rounded-full flex-shrink-0" style={getTierBadgeStyle(item.tier_required)}>
                            {item.tier_required}
                          </span>
                        </div>
                        {item.description && (
                          <p className="text-sm line-clamp-1" style={{ color: '#a3a3a3' }}>{item.description}</p>
                        )}
                      </div>
                      {item.type === 'quiz' ? (
                        <Button
                          size="sm"
                          onClick={() => window.open(item.url, '_blank')}
                          className="flex items-center gap-2"
                          style={{ background: 'rgba(212, 175, 55, 0.2)', color: '#D4AF37' }}
                        >
                          <HelpCircle size={16} /> Take Quiz
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => item.type === 'pdf' ? window.open(item.url, '_blank') : openViewer(item.url, item.type, item.title)}
                          className="flex items-center gap-2"
                          style={{ background: 'rgba(212, 175, 55, 0.2)', color: '#D4AF37' }}
                        >
                          {item.type === 'pdf' ? <Eye size={16} /> : <Play size={16} />}
                          {item.type === 'pdf' ? 'View' : 'Play'}
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              ) : currentFolderId !== null ? (
                <div className="text-center py-12">
                  <FolderOpen size={48} className="mx-auto mb-4" style={{ color: '#666' }} />
                  <p style={{ color: '#a3a3a3' }}>This folder is empty</p>
                </div>
              ) : (
                <div className="text-center py-12">
                  <p style={{ color: '#a3a3a3' }}>No content in this location. Browse the folders above.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Media Viewer */}
      <MediaViewer
        isOpen={viewerOpen}
        onClose={() => setViewerOpen(false)}
        url={viewerData.url}
        type={viewerData.type}
        title={viewerData.title}
      />
    </div>
  );
};

export default ContentLibrary;
